import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ConfigAuthValues } from '../types';
import {
  BFF_ORDERS,
  BFF_STORES,
  BFF_V1,
  API_FETCH_ERROR,
  API_FETCH_FULLFILLED,
  API_FETCH_PENDING,
  API_FETCH_READY,
  VERIFIED_ORDERS,
  BFF_PICK_TOURS,
  BFF_USERS,
  PICKING_APIGEE_BASE_PATH,
} from '../constants';
import { APIPickTour } from '../redux/sagas/types';
import { createToast } from '../redux/actions/toasts.actions';
import useFetch from './useFetch';

/* 
this custom hook works as a middleware to validate if picktour is already in progress by another user
and if so, display who´s working on in
*/
export default function() {
  const [status, setStatus] = useState({ pickTourID: '', status: API_FETCH_READY });
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { get } = useFetch();

  async function getUser(configAuthValues: ConfigAuthValues, pickTourID: string) {
    let user;
    const idResult = await get(`${configAuthValues.APIGEE_BASE_URL}${PICKING_APIGEE_BASE_PATH}${BFF_V1}${BFF_PICK_TOURS}/${pickTourID}`);
    if (idResult.json) {
      user = idResult.json.pickTour?.assignedPickerEmployeeID;
    }
    if (user) {
      const userResult = await get(`${configAuthValues.APIGEE_BASE_URL}${PICKING_APIGEE_BASE_PATH}${BFF_V1}${BFF_USERS}/${user}`);
      if (userResult.json) user = userResult.json.preferredName || user;
    }
    return user;
  }

  async function verify(
    storeID: string | null,
    configAuthValues: ConfigAuthValues | null,
    pickTour: APIPickTour,
    callback = () => {},
    onFail = () => {}
  ) {
    const { pickTourID } = pickTour;
    setStatus({ pickTourID, status: API_FETCH_PENDING });
    if (storeID && configAuthValues) {
      const ordersResponse = await get(`${configAuthValues.APIGEE_BASE_URL}${PICKING_APIGEE_BASE_PATH}${BFF_V1}${BFF_STORES}/${storeID}${BFF_ORDERS}`);
      if (ordersResponse.json) {
        const { pickTours: availablePickTours, editingOrderIds = [] } = ordersResponse.json;
        const availablePickTourIDs = availablePickTours.map((tour: APIPickTour) => tour.pickTourID);
        const isPickTourAvailable = availablePickTourIDs.includes(pickTourID);

        if (isPickTourAvailable) {
          callback();
        } else {
          localStorage.setItem(VERIFIED_ORDERS, JSON.stringify(ordersResponse.json));

          // Extract all order IDs from the pick tour metadata
          const pickTourOrderIDs = pickTour.metadata?.ordersSummaryData?.map(order => order.orderID) || [];

          // Check if all orders in the pick tour are currently being edited by another user
          const areAllOrdersBeingEdited =
            pickTourOrderIDs.length > 0 &&
            pickTourOrderIDs.every(orderID => editingOrderIds?.includes(orderID));

          onFail();

          if (areAllOrdersBeingEdited) {
            // All orders in pick tour are being edited - show ORDER_BEING_EDITED error
            dispatch(
              createToast({
                type: 'error',
                message: t('ERRORS.ORDER_BEING_EDITED'),
                error: {
                  id: '',
                  code: 'GENERIC_NON_SERVICE_ERROR',
                  detail: 'ORDER_BEING_EDITED',
                },
              })
            );
          } else {
            // Pick tour is in progress by another user - show PICKTOUR_IN_PROGRESS error with user info
            let assignedUser = await getUser(configAuthValues, pickTourID);
            assignedUser = assignedUser ? `${t('UTILS.USER')} ${assignedUser}` : t('UTILS.OTHER_USER');
            dispatch(
              createToast({ type: 'error', message: t('ERRORS.PICKTOUR_IN_PROGRESS').replace('_user_', assignedUser) })
            );
          }
        }
        setStatus({ pickTourID: '', status: API_FETCH_FULLFILLED });
      }

      if (ordersResponse.error) {
        setStatus({ pickTourID: '', status: API_FETCH_ERROR });
        onFail();
        dispatch(createToast({ type: 'error', message: t('ERRORS.DEFAULT_ERROR_MESSAGE') }));
      }
    } else {
      setStatus({ pickTourID: '', status: API_FETCH_ERROR });
      onFail();
      dispatch(createToast({ type: 'error', message: t('ERRORS.DEFAULT_ERROR_MESSAGE') }));
    }
  }
  return { status, verify };
}
